/**
 * PROTOTYPE — persistent-parent voxel layer.
 *
 * Why this exists
 * ---------------
 * Today every voxel data layer (Elevation, AGT, Profit, Harvest, …) is its own
 * MVTLayer pointing at the SAME tile endpoint (`data_pbf_path`). They differ
 * only in how they color/extrude identical voxel geometry. That means:
 *   - the same tiles are fetched + cached N times (one cache per active layer), and
 *   - switching layers tears down one MVTLayer and builds another, which on
 *     deck.gl 8.4 leaks GPU buffers in finalize() (JS heap recovers, GPU does not).
 *
 * This layer is a single MVTLayer that loads the voxel tiles ONCE and renders a
 * GeoJsonLayer sub-layer per mode inside `renderSubLayers`. Crucially, every mode's
 * sub-layer is ALWAYS returned and merely toggled with `visible` — it is never
 * removed from the sub-layer array — so toggling a layer on/off does not finalize
 * anything and never hits the leaky GPU-disposal path. The parent tileset also
 * persists across toggles, so no tiles are re-fetched on a switch.
 *
 * Tradeoff (read before shipping)
 * -------------------------------
 * Keeping every mode's sub-layer mounted means each mode keeps its own GPU
 * geometry resident (deck.gl can't share vertex buffers across sub-layers), even
 * while hidden. Memory is therefore HIGHER at baseline but STABLE — which is the
 * whole point: stable-high doesn't crash, unbounded-growth does. To cap the
 * baseline, `buildModeConfigs` should only include modes that actually have data
 * (and you can further restrict to "available" modes). If true multi-mode stacking
 * turns out NOT to be needed, the cheaper alternative is a SINGLE GeoJsonLayer
 * whose getFillColor dispatches to the one active mode — see note at the bottom.
 *
 * Status: all voxel data modes are implemented — Elevation, AGT, Harvest,
 * Fertilizer (as-applied), Planting, YieldByElevation, Profit, AGTByElevation,
 * ProfitByAGT — and wired into buildLayers (Map.js) behind VOXEL_PROTOTYPE_ENABLED.
 * The color/extrusion logic is ported from the matching classes in layers.js; the
 * selector still reads state.layers (the old per-mode layers are kept there for
 * the selector but excluded from deck by VueDeckGL). Not yet validated at runtime.
 */

import * as d3 from "d3"
import { GeoJsonLayer } from "@deck.gl/layers"
import { CROPS, MAP_LAYER } from "@/constants"
import { getAgtColorRGB, findMinMax } from "@/utility"
import {
  AuthMVTLayer,
  getRangesBasedOnStdDeviationElevation,
  getRangeBasedOnStdDeviationGeneric,
  getRangesBasedOnStdDeviationYield,
  ELEVATION_COLORS,
  HARVEST_YIELD_COLORS,
} from "@/components/map/layers"

// --- prototype wiring (temporary; flip the flag off to fully restore the old path) ---
// While enabled, buildLayers mounts ONE VoxelLayer that renders the modes below,
// and VueDeckGL excludes the old per-mode MVTLayers for those same modes from deck
// (so they don't double-render or re-introduce the leak). The old layers stay in
// state.layers so the selector still shows/toggles them.
export const VOXEL_PROTOTYPE_ENABLED = true
// Every voxel data mode the VoxelLayer now renders. The matching old per-mode
// MVTLayers are excluded from deck (VueDeckGL) but kept in state.layers so the
// selector still shows/toggles them.
export const VOXEL_PROTOTYPE_MODE_IDS = [
  MAP_LAYER.Elevation,
  MAP_LAYER.AGT,
  MAP_LAYER.Harvest,
  MAP_LAYER.Fertilizer,
  MAP_LAYER.Planting,
  MAP_LAYER.YieldByElevation,
  MAP_LAYER.Profit,
  MAP_LAYER.AGTByElevation,
  MAP_LAYER.ProfitByAGT,
]
export const VOXEL_LAYER_ID = "voxel-prototype"

// Don't load voxel tiles below this zoom. At low zoom a single MVT tile spans a
// huge area, so with many fields it carries millions of per-voxel polygons —
// gigabytes of GeoJSON in the JS heap, which is what crashes the tab at zoom 11.
// deck.gl's getTileIndices returns NO tiles below minZoom *when extent is unset*
// (with extent it clamps up and over-fetches instead), so VoxelLayer also drops
// extent. Tune this up if zoomed-in tiles are still too heavy. Voxels simply
// don't render below this zoom — that's the tradeoff for keeping the tab alive.
export const VOXEL_MIN_ZOOM = 8

// --- small helpers (mirrored from layers.js; dedupe into one module at integration) ---

const mapElevation = 200

const normalizeData = (value, min, max, scale) => {
  const delta = max - min
  if (!delta) return 0
  return Math.round(((value - min) / delta) * scale)
}

// O(1) field-membership. The parent computes the Set once per build and passes
// it down so each feature's color eval is a Set.has() instead of Array.includes.
const isActive = (activeFieldIdSet, fieldId) =>
  !activeFieldIdSet || activeFieldIdSet.size === 0 || activeFieldIdSet.has(fieldId)

const parseAgt27 = properties => {
  if (!("_agt27" in properties)) {
    properties._agt27 = JSON.parse(properties.agts)["27"]
  }
  return properties._agt27
}

// --- per-mode color / elevation functions (moved verbatim from the layer classes) ---

const colorElevation = (properties, valueRange, activeFieldIdSet) => {
  const { fieldId, elevationMeters } = properties
  const featValue = elevationMeters
  if (
    featValue <= 0
    || featValue === null
    || featValue == undefined
    || valueRange == undefined
  )
    return [0, 0, 0, 0]

  const colorIdx
    = valueRange.length
      - valueRange.filter(cutoffVal => featValue <= cutoffVal).length
  if (colorIdx > 8) return [0, 0, 0, 0]

  const returnColor = ELEVATION_COLORS[colorIdx]
  const opacity = isActive(activeFieldIdSet, fieldId) ? 255 : 0
  return [...returnColor, opacity]
}

// 3D extrusion height. `outputScale` folds in both the base height (mapElevation,
// or maxElevation/maxProfit for the *ByElevation/ByAGT modes) and the per-layer
// elevation scale, matching what the old constructors + deck elevationScale did.
const extrudeMeters = (properties, min, max, outputScale) => {
  let e = Math.min(max, properties.elevationMeters)
  e = Math.max(min, e)
  return normalizeData(e, min, max, outputScale)
}

const extrudeProfit = (properties, min, max, outputScale, clamp) => {
  let p = properties.roiProfit
  if (clamp) {
    p = Math.min(max, p)
    p = Math.max(min, p)
  }
  return normalizeData(p, min, max, outputScale)
}

const colorAgt = (properties, activeFieldIdSet) => {
  const { fieldId } = properties
  const agt = parseAgt27(properties)
  if (agt == undefined) return [0, 0, 0, 0]
  const opacity = isActive(activeFieldIdSet, fieldId) ? 255 : 0
  return [...getAgtColorRGB(agt), opacity]
}

// Harvest and YieldByElevation color identically; only the precomputed
// valueRanges differ (harvest = generic ranges, yield = yield ranges).
const colorYield = (
  properties,
  valueRanges,
  includeDefaults,
  targetCropId,
  activeFieldIdSet
) => {
  const { yieldPerAcreNew, yieldPerAcreDefaultsNew, fieldId } = properties
  let yieldPerAcre = includeDefaults ? yieldPerAcreDefaultsNew : yieldPerAcreNew
  if (yieldPerAcre == undefined) return [0, 0, 0, 0]
  yieldPerAcre = JSON.parse(yieldPerAcre)
  if (yieldPerAcre <= 0 || yieldPerAcre === null) return [0, 0, 0, 0]

  const crop
    = targetCropId == "All" || targetCropId == undefined
      ? Object.keys(yieldPerAcre[0])[0]
      : String(targetCropId)

  yieldPerAcre = yieldPerAcre
    .filter(e => Object.keys(e).includes(crop))
    .map(j => j[crop])
  if (yieldPerAcre.length > 0) yieldPerAcre = yieldPerAcre[0]
  else return [0, 0, 0, 0]
  if (yieldPerAcre <= 0 || yieldPerAcre === null || yieldPerAcre == undefined)
    return [0, 0, 0, 0]

  const valueRange = valueRanges[crop]
  if (valueRange == undefined) return [0, 0, 0, 0]
  const colorIdx
    = valueRange.length - valueRange.filter(c => yieldPerAcre <= c).length
  if (colorIdx > 8) return [0, 0, 0, 0]

  const color = [...HARVEST_YIELD_COLORS[colorIdx]]
  color[3] = isActive(activeFieldIdSet, fieldId) ? 255 : 0
  return color
}

const PLANTING_CUTOFFS = {
  [CROPS.Corn]: [20000, 23000, 26000, 28000, 30000, 32000, 35000, 38000],
  [CROPS.Cotton]: [32000, 33250, 34500, 35750, 37000, 38250, 39500, 42000],
  [CROPS.Wheat]: [0.76, 0.93, 1.15, 1.29, 1.47, 1.79, 2.11, 2.43],
  [CROPS.Rice]: [200000, 300000, 400000, 450000, 500000, 600000, 700000, 800000],
  [CROPS.Soybeans]: [
    130000, 142000, 153000, 165000, 176000, 186000, 197000, 207000,
  ],
  [CROPS.BlackBeans]: [
    65000, 75000, 85000, 95000, 105000, 120000, 135000, 150000,
  ],
}

const PLANTING_COLORS = [
  [255, 0, 0, 255],
  [255, 92, 0, 255],
  [255, 130, 0, 255],
  [255, 213, 0, 255],
  [255, 255, 0, 255],
  [200, 230, 50, 255],
  [180, 250, 0, 255],
  [64, 255, 0, 255],
  [0, 255, 0, 255],
]

const colorPlanting = (
  properties,
  includeDefaults,
  targetCropId,
  activeFieldIdSet
) => {
  const { fieldId, plantingSeedsPerAcreNew, plantingSeedsPerAcreDefaultsNew }
    = properties
  let featValue = includeDefaults
    ? plantingSeedsPerAcreDefaultsNew
    : plantingSeedsPerAcreNew
  if (featValue == undefined || featValue == null) return [0, 0, 0, 0]
  featValue = JSON.parse(featValue)

  const crop
    = (targetCropId == "All" || targetCropId == undefined) && featValue.length > 0
      ? Object.keys(featValue[0])[0]
      : String(targetCropId)

  featValue = featValue.filter(e => Object.keys(e).includes(crop)).map(j => j[crop])
  if (featValue.length > 0) featValue = featValue[0]
  if (featValue <= 0 || featValue === null || featValue == undefined)
    return [0, 0, 0, 0]

  if (!(crop in PLANTING_CUTOFFS)) return [0, 0, 0, 0]
  let cutoff = PLANTING_CUTOFFS[crop]
  if (crop === CROPS.Rice && featValue < 2000)
    cutoff = [15, 18, 22, 25, 28, 32, 38, 42]

  const colorIdx = cutoff.length - cutoff.filter(c => featValue <= c).length
  if (colorIdx > 8) return [0, 0, 0, 0]

  const color = [...PLANTING_COLORS[colorIdx]]
  color[3] = isActive(activeFieldIdSet, fieldId) ? 255 : 0
  return color
}

const FERT_COLORS = [
  [255, 255, 255, 255],
  [232, 234, 255, 255],
  [209, 213, 255, 255],
  [181, 188, 255, 255],
  [157, 167, 255, 255],
  [134, 146, 255, 255],
  [97, 113, 255, 255],
  [70, 89, 255, 255],
  [42, 64, 255, 255],
]

const colorFertilizer = (
  properties,
  valueRanges,
  includeDefaults,
  targetCropId,
  activeFieldIdSet
) => {
  const { fertAmountPerAcreNew, fertAmountPerAcreDefaultsNew, fieldId }
    = properties
  let fertPerAcre = includeDefaults
    ? fertAmountPerAcreDefaultsNew
    : fertAmountPerAcreNew
  if (fertPerAcre == undefined) return [0, 0, 0, 0]
  fertPerAcre = JSON.parse(fertPerAcre)
  if (fertPerAcre <= 0 || fertPerAcre === null) return [0, 0, 0, 0]

  const crop
    = targetCropId == "All" || targetCropId == null
      ? "All"
      : String(targetCropId)

  if (crop != "All") {
    fertPerAcre = fertPerAcre
      .filter(e => Object.keys(e).includes(crop))
      .map(j => j[crop])
    if (fertPerAcre.length > 0) fertPerAcre = fertPerAcre[0]
  } else {
    fertPerAcre = fertPerAcre.map(e => Object.values(e)[0])
    if (fertPerAcre.length > 0)
      fertPerAcre = fertPerAcre.reduce((a, b) => a + b, 0)
  }
  if (fertPerAcre <= 0 || fertPerAcre === null || fertPerAcre == undefined)
    return [0, 0, 0, 0]

  const valueRange = valueRanges[crop]
  if (valueRange == undefined) return [0, 0, 0, 0]
  const colorIdx
    = valueRange.length - valueRange.filter(c => fertPerAcre <= c).length
  if (colorIdx > 8) return [0, 0, 0, 0]

  const color = [...FERT_COLORS[colorIdx]]
  color[3] = isActive(activeFieldIdSet, fieldId) ? 255 : 0
  return color
}

const PROFIT_COLORS = [
  [212, 61, 81, 255],
  [230, 106, 77, 255],
  [241, 148, 82, 255],
  [247, 227, 130, 255],
  [189, 207, 117, 255],
  [133, 185, 111, 255],
  [79, 161, 110, 255],
  [0, 135, 108, 255],
]

const colorProfit = (properties, colorScaleLinear, activeFieldIdSet) => {
  if (properties.roiProfit === null || properties.roiProfit == undefined)
    return [0, 0, 0, 0]
  const color = PROFIT_COLORS[Math.round(colorScaleLinear(properties.roiProfit))]
  if (color == null) return [0, 0, 0, 0]
  const out = [...color]
  out[3] = isActive(activeFieldIdSet, properties.fieldId) ? 255 : 0
  return out
}

// clipped (±2σ) min/max of a numeric property, matching the old constructors.
const numericProp = (features, key) =>
  features
    .filter(f => typeof f.properties[key] === "number" && f.properties[key])
    .map(f => f.properties[key])

const clippedMinMax = values => {
  const n = values.length || 1
  const mean = values.reduce((a, b) => a + b, 0) / n
  const sd = Math.sqrt(values.reduce((a, b) => a + (b - mean) ** 2, 0) / n)
  const clipped = values.filter(v => v > mean - sd * 2 && v < mean + sd * 2)
  return {
    min: clipped.reduce((p, c) => Math.min(p, c), Number.MAX_VALUE),
    max: clipped.reduce((p, c) => Math.max(p, c), 0),
  }
}

/**
 * Build the per-mode config the parent layer needs, from the voxel feature
 * collections already computed in buildLayers (constVoxelFeatures keyed by mode).
 * This is where each mode's color RANGE is precomputed once — the range no longer
 * lives in (and is no longer retained by) per-layer constructors.
 *
 * Returns: { [mode]: { range, ... } } for modes that have data.
 */
export const buildModeConfigs = (constVoxelFeatures, includeDefaults) => {
  const configs = {}
  const fc = mode => constVoxelFeatures[mode]
  const has = mode => fc(mode) && fc(mode).features.length

  // Elevation — color by elevation band, extrude by elevation.
  if (has(MAP_LAYER.Elevation)) {
    const features = fc(MAP_LAYER.Elevation).features
    configs[MAP_LAYER.Elevation] = {
      valueRange: getRangesBasedOnStdDeviationElevation(features),
      ...clippedMinMax(numericProp(features, "elevation")),
    }
  }

  // AGT — color straight from the agts property; no range, no extrusion.
  configs[MAP_LAYER.AGT] = {}

  // Harvest — yield color (generic ranges), flat. Gated on anyHarvest like before.
  if (has(MAP_LAYER.Harvest) && constVoxelFeatures.anyHarvest) {
    configs[MAP_LAYER.Harvest] = {
      valueRanges: getRangeBasedOnStdDeviationGeneric(
        fc(MAP_LAYER.Harvest).features,
        includeDefaults,
        "harvest"
      ),
    }
  }

  // YieldByElevation — yield color (yield ranges), extrude by elevation.
  if (has(MAP_LAYER.YieldByElevation) && constVoxelFeatures.anyHarvest) {
    const features = fc(MAP_LAYER.YieldByElevation).features
    configs[MAP_LAYER.YieldByElevation] = {
      valueRanges: getRangesBasedOnStdDeviationYield(features),
      ...clippedMinMax(numericProp(features, "elevation")),
    }
  }

  // Fertilizer (as-applied) — fert color, flat.
  if (has(MAP_LAYER.Fertilizer) && constVoxelFeatures.anyFert) {
    configs[MAP_LAYER.Fertilizer] = {
      valueRanges: getRangeBasedOnStdDeviationGeneric(
        fc(MAP_LAYER.Fertilizer).features,
        includeDefaults,
        "fert"
      ),
    }
  }

  // Planting — crop-cutoff color, flat, no precomputed range.
  if (has(MAP_LAYER.Planting) && constVoxelFeatures.anyPlanting) {
    configs[MAP_LAYER.Planting] = {}
  }

  // Profit — d3 color scale, extrude by profit (clipped min/max).
  if (has(MAP_LAYER.Profit)) {
    const features = fc(MAP_LAYER.Profit).features
    configs[MAP_LAYER.Profit] = {
      colorScale: d3
        .scaleLinear()
        .domain([-300, 300])
        .range([0, 7])
        .clamp(true),
      ...clippedMinMax(numericProp(features, "profit")),
    }
  }

  // AGTByElevation — AGT color, extrude by elevation (base = maxElevation).
  if (has(MAP_LAYER.AGTByElevation)) {
    const features = fc(MAP_LAYER.AGTByElevation).features
    configs[MAP_LAYER.AGTByElevation] = clippedMinMax(
      numericProp(features, "elevation")
    )
  }

  // ProfitByAGT — AGT color, extrude by profit. The old layer used raw min/max of
  // (profit || 0) with no ±2σ clipping, so we replicate that here.
  if (has(MAP_LAYER.ProfitByAGT)) {
    const profits = fc(MAP_LAYER.ProfitByAGT).features.map(d =>
      typeof d.properties.profit === "number" ? d.properties.profit : 0
    )
    const minmax = findMinMax(profits)
    configs[MAP_LAYER.ProfitByAGT] = { min: minmax[0], max: minmax[1] }
  }

  return configs
}

// --- mode → sub-layer builders ---
// Each builder returns a GeoJsonLayer for one mode. `visible` is the toggle; the
// builder is invoked for EVERY mode (with data) on EVERY tile so the sub-layer
// stays mounted and never gets finalized on a layer toggle.
//
// `props` here is the per-tile sub-layer props handed to us by MVTLayer's
// renderSubLayers — it already carries the tile data plus the coordinate
// transform (modelMatrix / coordinateOrigin / coordinateSystem / clip extension),
// AND every prop we passed to the parent VoxelLayer (activeLayerIds, opacities,
// modeConfigs, onHover, …). So we read mode state straight off `props`.

// A flat (non-extruded) fill sub-layer. Most modes share this shape.
const flatLayer = (props, suffix, mode, getFillColor, fillTriggers) =>
  new GeoJsonLayer({
    ...props,
    id: `${props.id}-${suffix}`,
    pickable: true,
    stroked: false,
    filled: true,
    extruded: false,
    visible: props.activeLayerIds.includes(mode),
    opacity: props.opacities[mode] ?? 1,
    getFillColor,
    getLineColor: () => [0, 0, 0, 0],
    lineWidthMinPixels: 0,
    updateTriggers: { getFillColor: fillTriggers },
  })

const MODE_BUILDERS = {
  [MAP_LAYER.Elevation]: props => {
    const cfg = props.modeConfigs[MAP_LAYER.Elevation]
    const scale = props.elevationScales[MAP_LAYER.Elevation] ?? 1
    return new GeoJsonLayer({
      ...props,
      id: `${props.id}-elevation`,
      pickable: true,
      stroked: false,
      filled: true,
      extruded: true,
      visible: props.activeLayerIds.includes(MAP_LAYER.Elevation),
      opacity: props.opacities[MAP_LAYER.Elevation] ?? 1,
      getFillColor: f =>
        colorElevation(f.properties, cfg.valueRange, props._activeFieldIdSet),
      getElevation: f =>
        extrudeMeters(f.properties, cfg.min, cfg.max, mapElevation * scale),
      getLineColor: () => [0, 0, 0, 0],
      lineWidthMinPixels: 1,
      updateTriggers: {
        getFillColor: [props._activeFieldIdsKey, cfg.valueRange],
        getElevation: [cfg.min, cfg.max, scale],
      },
    })
  },

  [MAP_LAYER.AGT]: props =>
    new GeoJsonLayer({
      ...props,
      id: `${props.id}-agt`,
      pickable: true,
      stroked: false,
      filled: true,
      extruded: false,
      visible: props.agtActive,
      opacity: props.opacities[MAP_LAYER.AGT] ?? 1,
      getFillColor: f => colorAgt(f.properties, props._activeFieldIdSet),
      getLineColor: () => [0, 0, 0, 0],
      lineWidthMinPixels: 0,
      updateTriggers: {
        getFillColor: [props._activeFieldIdsKey],
      },
    }),

  [MAP_LAYER.Harvest]: props => {
    const cfg = props.modeConfigs[MAP_LAYER.Harvest]
    return flatLayer(
      props,
      "harvest",
      MAP_LAYER.Harvest,
      f =>
        colorYield(
          f.properties,
          cfg.valueRanges,
          props.includeDefaults,
          props.targetHarvestCrop,
          props._activeFieldIdSet
        ),
      [
        props._activeFieldIdsKey,
        props.includeDefaults,
        props.targetHarvestCrop,
        cfg.valueRanges,
      ]
    )
  },

  [MAP_LAYER.Fertilizer]: props => {
    const cfg = props.modeConfigs[MAP_LAYER.Fertilizer]
    return flatLayer(
      props,
      "fertilizer",
      MAP_LAYER.Fertilizer,
      f =>
        colorFertilizer(
          f.properties,
          cfg.valueRanges,
          props.includeDefaults,
          props.targetFertCrop,
          props._activeFieldIdSet
        ),
      [
        props._activeFieldIdsKey,
        props.includeDefaults,
        props.targetFertCrop,
        cfg.valueRanges,
      ]
    )
  },

  [MAP_LAYER.Planting]: props =>
    flatLayer(
      props,
      "planting",
      MAP_LAYER.Planting,
      f =>
        colorPlanting(
          f.properties,
          props.includeDefaults,
          props.targetSeedCrop,
          props._activeFieldIdSet
        ),
      [props._activeFieldIdsKey, props.includeDefaults, props.targetSeedCrop]
    ),

  [MAP_LAYER.YieldByElevation]: props => {
    const cfg = props.modeConfigs[MAP_LAYER.YieldByElevation]
    const scale = props.elevationScales[MAP_LAYER.YieldByElevation] ?? 1
    return new GeoJsonLayer({
      ...props,
      id: `${props.id}-yield-by-elevation`,
      pickable: true,
      stroked: false,
      filled: true,
      extruded: true,
      visible: props.activeLayerIds.includes(MAP_LAYER.YieldByElevation),
      opacity: props.opacities[MAP_LAYER.YieldByElevation] ?? 1,
      getFillColor: f =>
        colorYield(
          f.properties,
          cfg.valueRanges,
          props.includeDefaults,
          props.targetHarvestCrop,
          props._activeFieldIdSet
        ),
      getElevation: f =>
        extrudeMeters(f.properties, cfg.min, cfg.max, mapElevation * scale),
      getLineColor: () => [0, 0, 0, 0],
      lineWidthMinPixels: 0,
      updateTriggers: {
        getFillColor: [
          props._activeFieldIdsKey,
          props.includeDefaults,
          props.targetHarvestCrop,
          cfg.valueRanges,
        ],
        getElevation: [cfg.min, cfg.max, scale],
      },
    })
  },

  [MAP_LAYER.Profit]: props => {
    const cfg = props.modeConfigs[MAP_LAYER.Profit]
    const scale = props.elevationScales[MAP_LAYER.Profit] ?? 1
    return new GeoJsonLayer({
      ...props,
      id: `${props.id}-profit`,
      pickable: true,
      stroked: false,
      filled: true,
      extruded: true,
      visible: props.activeLayerIds.includes(MAP_LAYER.Profit),
      opacity: props.opacities[MAP_LAYER.Profit] ?? 1,
      getFillColor: f =>
        colorProfit(f.properties, cfg.colorScale, props._activeFieldIdSet),
      getElevation: f =>
        extrudeProfit(f.properties, cfg.min, cfg.max, mapElevation * scale, true),
      getLineColor: () => [0, 0, 0, 0],
      lineWidthMinPixels: 1,
      updateTriggers: {
        getFillColor: [props._activeFieldIdsKey],
        getElevation: [cfg.min, cfg.max, scale],
      },
    })
  },

  [MAP_LAYER.AGTByElevation]: props => {
    const cfg = props.modeConfigs[MAP_LAYER.AGTByElevation]
    const scale = props.elevationScales[MAP_LAYER.AGTByElevation] ?? 1
    return new GeoJsonLayer({
      ...props,
      id: `${props.id}-agt-by-elevation`,
      pickable: true,
      stroked: false,
      filled: true,
      extruded: true,
      visible: props.activeLayerIds.includes(MAP_LAYER.AGTByElevation),
      opacity: props.opacities[MAP_LAYER.AGTByElevation] ?? 1,
      getFillColor: f => colorAgt(f.properties, props._activeFieldIdSet),
      // base height is maxElevation (matches the old AGTByElevation constructor)
      getElevation: f =>
        extrudeMeters(f.properties, cfg.min, cfg.max, cfg.max * scale),
      getLineColor: () => [0, 0, 0, 0],
      lineWidthMinPixels: 1,
      updateTriggers: {
        getFillColor: [props._activeFieldIdsKey],
        getElevation: [cfg.min, cfg.max, scale],
      },
    })
  },

  [MAP_LAYER.ProfitByAGT]: props => {
    const cfg = props.modeConfigs[MAP_LAYER.ProfitByAGT]
    const scale = props.elevationScales[MAP_LAYER.ProfitByAGT] ?? 1
    return new GeoJsonLayer({
      ...props,
      id: `${props.id}-profit-by-agt`,
      // the old ProfitByAgt layer was not pickable
      pickable: false,
      stroked: false,
      filled: true,
      extruded: true,
      visible: props.activeLayerIds.includes(MAP_LAYER.ProfitByAGT),
      opacity: props.opacities[MAP_LAYER.ProfitByAGT] ?? 1,
      getFillColor: f => colorAgt(f.properties, props._activeFieldIdSet),
      // base height is maxProfit, roiProfit not clamped (matches old constructor)
      getElevation: f =>
        extrudeProfit(f.properties, cfg.min, cfg.max, cfg.max * scale, false),
      getLineColor: () => [0, 0, 0, 0],
      lineWidthMinPixels: 1,
      updateTriggers: {
        getFillColor: [props._activeFieldIdsKey],
        getElevation: [cfg.min, cfg.max, scale],
      },
    })
  },
}

// renderSubLayers PROP. MVTLayer's renderSubLayers METHOD runs first (applying the
// tile coordinate transform), then TileLayer delegates to this prop with the
// corrected sub-props — so we never replicate deck.gl's tile math.
const buildVoxelSubLayers = props => {
  const configs = props.modeConfigs || {}
  const subLayers = []
  for (const mode of Object.keys(MODE_BUILDERS)) {
    // Only mount modes that have data/config. Modes never mounted can't leak,
    // and this caps the resident-geometry baseline (see file header tradeoff).
    if (!configs[mode]) continue
    subLayers.push(MODE_BUILDERS[mode](props))
  }
  return subLayers
}

/**
 * The persistent parent. Mount ONE of these (never swap it out) whenever MVT
 * voxel mode is active. Pass it the active modes + per-mode params and it renders
 * a toggled sub-layer per mode over a single shared tileset.
 */
// Extends AuthMVTLayer, which supplies the worker-safe auth header, binary:false,
// and the map-idle gate (see layers.js).
class VoxelLayer extends AuthMVTLayer {
  constructor(props) {
    super({
      ...props,
      // Precompute the field-id Set once per build (not per feature, not per
      // mode). `_activeFieldIdsKey` is a cheap identity for updateTriggers, since
      // a Set isn't compared structurally.
      _activeFieldIdSet: new Set(props.activeFieldIds || []),
      _activeFieldIdsKey: (props.activeFieldIds || []).join(","),
      minZoom: VOXEL_MIN_ZOOM,
      maxZoom: 16,
      // extent MUST be unset for the minZoom floor to skip loading below it
      // (with an extent, deck clamps up to minZoom and over-fetches instead).
      extent: null,
      renderSubLayers: buildVoxelSubLayers,
    })
  }
}

export default VoxelLayer
export { VoxelLayer }

/**
 * Integration (next step, once the approach is validated)
 * -------------------------------------------------------
 * 1. buildLayers (Map.js, MVT branch): replace the ~9 per-mode `new XLayer(...)`
 *    blocks with a single:
 *      layers[MAP_LAYER.Voxel] = new VoxelLayer({
 *        id: MAP_LAYER.Voxel,
 *        data: data_pbf_path,
 *        extent: extentArray,
 *        maxCacheSize,
 *        activeLayerIds,
 *        agtActive: agtCrops.length > 0,
 *        activeFieldIds,
 *        opacities: <scaleOpacity-applied map>,
 *        elevationScales: <scaleElevationScale-applied map>,
 *        modeConfigs: buildModeConfigs(constVoxelFeatures),
 *        onHover, onClick,
 *      })
 *    Keep Boundary / FieldSelection / Crops / Microclimate as they are.
 *
 * 2. Selector (Map.js getters getArvaLayers/getDataLayers + LayerController):
 *    these currently map over `state.layers`, which would now hold ONE voxel
 *    layer. Drive the selector from a STATIC list of mode descriptors instead, so
 *    it stays fully populated regardless of what's mounted. Toggling a row keeps
 *    setting activeLayerIds (unchanged); the VoxelLayer reads it to flip sub-layer
 *    `visible`.
 *
 * 3. Hover/click: onHover/onClick are forwarded to every sub-layer; the feature
 *    `properties` are identical to today, so the existing handlers in buildLayers
 *    work unchanged.
 *
 * Cheaper alternative if multi-mode stacking is NOT actually required:
 * one GeoJsonLayer sub-layer whose getFillColor dispatches on the single active
 * mode (and getElevation/extruded set from it). No per-mode geometry duplication,
 * but only one coloring at a time.
 */
